import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Globe2, Building2, Users, Award,
  Cloud, GitBranch, CloudCog, Activity,
  Workflow, FileCode2, LineChart, ShieldCheck,
  Video, Building, Laptop, Layers,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Globe2, value: "25+", label: "Global Technology Partners", desc: "Delivering enterprise learning aligned with leading cloud platforms and industry technologies." },
  { icon: Building2, value: "200+", label: "Corporate Learning Programs", desc: "Customized Cloud & DevOps learning paths tailored to business objectives." },
  { icon: Users, value: "100+", label: "Enterprise Trainers", desc: "Experienced cloud architects and DevOps experts delivering practical, hands-on training." },
  { icon: Award, value: "250+", label: "Enterprise Clients", desc: "Trusted by organizations worldwide to build cloud-first and DevOps-driven teams." }
];

const LEARNING_OUTCOMES = [
  { icon: Cloud, title: "Cloud Infrastructure", desc: "Gain practical expertise in designing, deploying, and managing secure cloud infrastructure across enterprise environments using modern cloud architecture and best practices." },
  { icon: GitBranch, title: "DevOps Practices", desc: "Master modern DevOps methodologies, including Continuous Integration (CI), Continuous Delivery (CD), infrastructure automation, and collaborative development workflows." },
  { icon: CloudCog, title: "Google Cloud Platform (GCP)", desc: "Develop hands-on experience with Google Cloud Platform (GCP) services, cloud-native deployments, networking, identity management, and enterprise cloud operations." },
  { icon: Activity, title: "Site Reliability Engineering (SRE)", desc: "Learn Site Reliability Engineering principles such as SLIs, SLOs, error budgets, incident management, and operational excellence to maintain reliable production systems." },
  { icon: Workflow, title: "CI/CD Pipeline Automation", desc: "Design, implement, and optimize secure CI/CD pipelines for application delivery, infrastructure provisioning, automated testing, and release management." },
  { icon: FileCode2, title: "Infrastructure as Code (IaC)", desc: "Automate infrastructure provisioning and configuration using Infrastructure as Code (IaC) practices with tools such as Terraform for consistent and scalable deployments." },
  { icon: LineChart, title: "Cloud Observability & Monitoring", desc: "Monitor applications and infrastructure using logging, metrics, dashboards, alerts, and observability practices to improve performance and quickly resolve operational issues." },
  { icon: ShieldCheck, title: "DevSecOps & Cloud Security", desc: "Integrate security throughout the DevOps lifecycle with secure deployment pipelines, policy enforcement, secrets management, vulnerability management, and cloud security best practices." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive instructor-led online sessions with live demonstrations, cloud labs, and real-world deployment scenarios." },
  { icon: Building, title: "Classroom Bootcamps", desc: "Hands-on enterprise workshops conducted onsite or at Skillsgain training centers." },
  { icon: Laptop, title: "Self-Paced Learning", desc: "Access learning resources, cloud labs, assessments, and recorded sessions anytime, anywhere." },
  { icon: Layers, title: "Blended Learning", desc: "Combine instructor-led training, self-paced modules, practical labs, and enterprise projects for maximum learning impact." }
];

const BY_THE_NUMBERS = [
  { value: "500+", label: "Instructor-Led Workshops" },
  { value: "500+", label: "Enterprise Batches Delivered" },
  { value: "100,000+", label: "Learning Hours Delivered" },
  { value: "30+", label: "Emerging Technology Domains" }
];

const CloudDevOpsPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE CLOUD & DEVOPS SOLUTIONS"
        title="Enterprise Cloud & DevOps Training to Accelerate Digital Transformation"
        subtitle="Empower your workforce with industry-focused Cloud & DevOps training designed to build expertise in Cloud Computing, Google Cloud Platform (GCP), Cloud Infrastructure, Cloud Storage, Continuous Integration (CI), and modern DevOps practices. Skillsgain helps enterprises develop cloud-ready teams through hands-on learning, real-world projects, and customized corporate training programs."
      />

      {/* Enterprise Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">ENTERPRISE IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Enabling organizations to scale faster with Cloud & DevOps expertise</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Cloud & DevOps Expertise */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">CLOUD & DEVOPS EXPERTISE</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Build cloud-native capabilities that power business innovation</h2>
            <p className="mt-3 text-gray-600">Develop practical cloud engineering and DevOps skills through instructor-led workshops, hands-on labs, and enterprise-focused learning designed for real-world implementation.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Flexible Enterprise Learning */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">FLEXIBLE ENTERPRISE LEARNING</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Choose the learning model that fits your cloud transformation journey</h2>
              <p className="mt-3 text-gray-600">Whether you're onboarding cloud engineers or upskilling enterprise teams, Skillsgain offers scalable learning experiences that align with your business needs.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + Customer Success Story */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#F57C20] mb-4"/>
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">CUSTOMER SUCCESS STORY</div>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain partnered with us to deliver a customized Cloud & DevOps learning program that enabled our engineering teams to adopt cloud-native practices, automate deployments, and improve operational efficiency. The practical labs and enterprise-focused curriculum accelerated our cloud transformation initiatives.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR ENTERPRISE TEAMS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Future-Proof Your Workforce with Cloud & DevOps Skills</h2>
                <p className="mt-3 text-gray-300">Build high-performing engineering teams with enterprise training in Cloud Computing, Google Cloud Platform (GCP), Cloud Infrastructure, Cloud Storage, Continuous Integration, and modern DevOps practices. Skillsgain delivers customized learning solutions that accelerate digital transformation and business growth.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Cloud Experts</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default CloudDevOpsPage;
